import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Slide,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { makeRequest } from "../../../assets/functions_helper";

// ── Transición ───────────────────────────────────────────────────────────────
const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ── Estilos ──────────────────────────────────────────────────────────────────
const FONT = "'Inter', 'Como', sans-serif";
const RED       = "#F72E41";
const RED_DK    = "#c0001d";
const RED_LIGHT = "#fff5f5";
const RED_BORDER = "rgba(247,46,65,0.25)";

// Lista de lo que se elimina (advertencia visual)
const DESTRUCTION_LIST = [
  { icon: "🪑", label: "Todas las mesas del restaurante" },
  { icon: "🍽️", label: "Menús, productos y categorías" },
  { icon: "📅", label: "Reservas activas y pendientes" },
  { icon: "👥", label: "Cuentas del personal asignado" },
  { icon: "🖼️", label: "Imágenes y archivos en Storage" },
];

/**
 * DeleteRestaurantModal
 *
 * Props:
 *   open           {boolean}    — controla visibilidad
 *   onClose        {fn}         — cierra el modal
 *   restaurant     {object}     — { id, name } del restaurante a eliminar
 *   onSuccess      {fn}         — callback tras eliminar con éxito
 */
export default function DeleteRestaurantModal({
  open,
  onClose,
  restaurant,
  onSuccess,
}) {
  const [confirmName, setConfirmName]   = useState("");
  const [isLoading, setIsLoading]       = useState(false);
  const [snack, setSnack]               = useState({ open: false, msg: "", severity: "error" });

  const restaurantName = restaurant?.name || "";
  const isConfirmed    = confirmName.trim() === restaurantName.trim();

  // ── Reset al cerrar ─────────────────────────────────────────────────────────
  const handleClose = () => {
    if (isLoading) return;
    setConfirmName("");
    onClose();
  };

  // ── Delete request ──────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!isConfirmed || !restaurant?.id) return;

    setIsLoading(true);

    try {
      await makeRequest(
        "DELETE",
        `restaurant_admin/restaurants/${restaurant.id}`,
        null,
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType"),
        null,
        true
      );

      setSnack({ open: true, msg: `"${restaurantName}" eliminado correctamente.`, severity: "success" });
      setConfirmName("");
      onClose();
      if (typeof onSuccess === "function") onSuccess(restaurant.id);

    } catch (err) {
      const msg =
        typeof err === "string"
          ? err
          : err?.message || "No se pudo eliminar el restaurante. Inténtalo de nuevo.";
      setSnack({ open: true, msg, severity: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={SlideUp}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            maxWidth: 480,
            width: "100%",
            fontFamily: FONT,
            overflow: "hidden",
            boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
          },
        }}
      >
        {/* ── Header de advertencia ─────────────────────────────────────────── */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1a0007 0%, #3b0010 100%)",
            px: 3,
            py: 3.5,
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "14px",
              background: "rgba(247,46,65,0.2)",
              border: "2px solid rgba(247,46,65,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              mt: 0.25,
            }}
          >
            <DeleteForeverIcon sx={{ color: "#f87171", fontSize: 26 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 800,
                fontSize: 17,
                fontFamily: FONT,
                lineHeight: 1.25,
              }}
            >
              Eliminar restaurante
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.6)",
                fontSize: 12,
                fontFamily: FONT,
                mt: 0.5,
                lineHeight: 1.55,
              }}
            >
              Esta acción es{" "}
              <Box
                component="span"
                sx={{ color: "#f87171", fontWeight: 700 }}
              >
                permanente e irreversible
              </Box>
              . No podrá deshacerse.
            </Typography>
          </Box>
        </Box>

        <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
          {/* ── Nombre del restaurante ─────────────────────────────────────── */}
          <Box
            sx={{
              background: RED_LIGHT,
              border: `1.5px solid ${RED_BORDER}`,
              borderRadius: "12px",
              px: 2,
              py: 1.5,
              mb: 2.5,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <WarningAmberIcon sx={{ color: RED, fontSize: 20, flexShrink: 0 }} />
            <Typography
              sx={{ fontSize: 13, color: RED_DK, fontFamily: FONT, fontWeight: 600 }}
            >
              Vas a eliminar:{" "}
              <Box component="span" sx={{ fontWeight: 900, fontSize: 14 }}>
                {restaurantName}
              </Box>
            </Typography>
          </Box>

          {/* ── Lista de consecuencias ─────────────────────────────────────── */}
          <Typography
            sx={{
              fontSize: 12,
              color: "#64748b",
              fontFamily: FONT,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              mb: 1.5,
            }}
          >
            Se eliminará permanentemente:
          </Typography>
          <Box
            sx={{
              background: "#fafafa",
              border: "1.5px solid #f1f5f9",
              borderRadius: "12px",
              px: 2,
              py: 1.5,
              mb: 3,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {DESTRUCTION_LIST.map(({ icon, label }) => (
              <Box
                key={label}
                sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
              >
                <Box sx={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>{icon}</Box>
                <Typography sx={{ fontSize: 13, color: "#374151", fontFamily: FONT }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* ── Campo de confirmación ──────────────────────────────────────── */}
          <Typography
            sx={{ fontSize: 13, color: "#374151", fontFamily: FONT, mb: 1, lineHeight: 1.6 }}
          >
            Para confirmar, escribe exactamente el nombre del restaurante:
          </Typography>
          <Box
            sx={{
              background: "#f8fafc",
              border: "1.5px solid #e2e8f0",
              borderRadius: "10px",
              px: 2,
              py: 1,
              mb: 1.5,
              display: "inline-block",
            }}
          >
            <Typography
              sx={{
                fontFamily: "'Courier New', monospace",
                fontSize: 14,
                fontWeight: 700,
                color: "#0f172a",
                userSelect: "all",
              }}
            >
              {restaurantName}
            </Typography>
          </Box>

          <TextField
            fullWidth
            placeholder={`Escribe "${restaurantName}"`}
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            disabled={isLoading}
            autoComplete="off"
            inputProps={{ spellCheck: false }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                fontFamily: FONT,
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: isConfirmed ? "#22c55e" : RED,
                  borderWidth: 2,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: isConfirmed ? "#22c55e" : RED_BORDER,
                },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: confirmName
                  ? isConfirmed
                    ? "#bbf7d0"
                    : "rgba(247,46,65,0.35)"
                  : "#e2e8f0",
                transition: "border-color 0.2s ease",
              },
            }}
            helperText={
              confirmName && !isConfirmed
                ? "⚠️ El nombre no coincide exactamente"
                : isConfirmed
                ? "✓ Nombre verificado"
                : " "
            }
            FormHelperTextProps={{
              sx: {
                fontFamily: FONT,
                fontSize: 12,
                color: confirmName
                  ? isConfirmed
                    ? "#16a34a"
                    : RED
                  : "transparent",
                transition: "color 0.2s ease",
              },
            }}
          />
        </DialogContent>

        {/* ── Acciones ──────────────────────────────────────────────────────── */}
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
          <Button
            onClick={handleClose}
            disabled={isLoading}
            sx={{
              flex: 1,
              background: "#f8fafc",
              color: "#475569",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              fontWeight: 700,
              fontSize: 14,
              py: 1.2,
              fontFamily: FONT,
              textTransform: "none",
              "&:hover": { borderColor: "#cbd5e1", background: "#f1f5f9" },
            }}
          >
            Cancelar
          </Button>

          <Button
            onClick={handleDelete}
            disabled={!isConfirmed || isLoading}
            sx={{
              flex: 2,
              background:
                !isConfirmed || isLoading
                  ? "#e2e8f0"
                  : `linear-gradient(135deg, ${RED}, ${RED_DK})`,
              color: !isConfirmed || isLoading ? "#94a3b8" : "#fff",
              borderRadius: "12px",
              fontWeight: 800,
              fontSize: 14,
              py: 1.2,
              fontFamily: FONT,
              textTransform: "none",
              boxShadow:
                !isConfirmed || isLoading
                  ? "none"
                  : "0 4px 14px rgba(247,46,65,0.4)",
              transition: "all 0.2s ease",
              "&:hover": {
                filter:
                  !isConfirmed || isLoading ? "none" : "brightness(1.08)",
              },
            }}
          >
            {isLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} sx={{ color: "#94a3b8" }} />
                <span>Eliminando…</span>
              </Box>
            ) : (
              "🗑️ Eliminar definitivamente"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} sx={{ borderRadius: "12px", fontFamily: FONT }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
